/**
 * Vitals reference ranges and interpretation.
 *
 * Mirrors the BE ReferenceRanges in
 * `novamed-api/src/services/vitalsService.js` and the LOINC code
 * constants in `novamed-clinic/src/services/vitalsService.ts`. Used to
 * surface inline warnings as the practitioner types and to drive the
 * abnormal-summary banner above Save.
 *
 * Critical thresholds match the BE: 80% / 120% of the normal bound.
 */

import { VitalLoinc } from '@/services/vitalsService';

export type Interpretation = 'N' | 'L' | 'H' | 'LL' | 'HH';

export interface RangeSpec {
  low: number;
  high: number;
  unit: string;
}

// Adult ranges. Keyed by LOINC code so this can be reused for any
// observation rendered from a backend response (e.g. trend tiles).
export const RANGES: Record<string, RangeSpec> = {
  [VitalLoinc.SYSTOLIC_BP]: { low: 90, high: 120, unit: 'mmHg' },
  [VitalLoinc.DIASTOLIC_BP]: { low: 60, high: 80, unit: 'mmHg' },
  [VitalLoinc.HEART_RATE]: { low: 60, high: 100, unit: 'bpm' },
  [VitalLoinc.RESPIRATORY_RATE]: { low: 12, high: 20, unit: '/min' },
  [VitalLoinc.BODY_TEMPERATURE]: { low: 97.8, high: 99.1, unit: '°F' },
  [VitalLoinc.OXYGEN_SATURATION]: { low: 95, high: 100, unit: '%' },
  [VitalLoinc.BMI]: { low: 18.5, high: 24.9, unit: 'kg/m²' },
};

/**
 * Returns the interpretation code for a given numeric value + LOINC.
 * Returns 'N' when the value is in range; null when no range is known.
 */
export const getInterpretation = (
  value: number | null | undefined,
  loinc: string,
): Interpretation | null => {
  if (value == null || !Number.isFinite(value)) return null;
  const range = RANGES[loinc];
  if (!range) return null;

  const criticalLow = range.low * 0.8;
  const criticalHigh = range.high * 1.2;

  if (value < criticalLow) return 'LL';
  if (value > criticalHigh) return 'HH';
  if (value < range.low) return 'L';
  if (value > range.high) return 'H';
  return 'N';
};

export const interpretationLabel = (i: Interpretation): string => {
  switch (i) {
    case 'LL': return 'Critical low';
    case 'HH': return 'Critical high';
    case 'L': return 'Low';
    case 'H': return 'High';
    case 'N': return 'Normal';
  }
};

export const isCritical = (i: Interpretation | null): boolean =>
  i === 'LL' || i === 'HH';

export const isAbnormal = (i: Interpretation | null): boolean =>
  i === 'L' || i === 'H' || i === 'LL' || i === 'HH';

// ============================================
// BMI categories (WHO classification)
// ============================================

export type BmiCategory =
  | 'Underweight'
  | 'Normal'
  | 'Overweight'
  | 'Obese I'
  | 'Obese II'
  | 'Obese III';

export const bmiCategory = (bmi: number): BmiCategory => {
  if (bmi < 18.5) return 'Underweight';
  if (bmi < 25) return 'Normal';
  if (bmi < 30) return 'Overweight';
  if (bmi < 35) return 'Obese I';
  if (bmi < 40) return 'Obese II';
  return 'Obese III';
};

// Tailwind classes for the BMI category chip.
export const bmiCategoryColor = (cat: BmiCategory): string => {
  switch (cat) {
    case 'Normal': return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    case 'Underweight': return 'text-amber-700 bg-amber-50 border-amber-200';
    case 'Overweight': return 'text-amber-700 bg-amber-50 border-amber-200';
    case 'Obese I': return 'text-orange-700 bg-orange-50 border-orange-200';
    case 'Obese II': return 'text-red-700 bg-red-50 border-red-200';
    case 'Obese III': return 'text-red-800 bg-red-100 border-red-300';
  }
};
